import { Dialog, DialogContent } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { BEEScorecardService } from "../../../Component/Api/BEEScorecard/BEEScorecard";
import { CoreStaticDataService } from "../../../Component/Api/StaticData/CoreStaticDataService";
import { UserService } from "../../../Component/Api/User/UserService";
import { ConfirmationDialog } from "../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { FilterType, IFilter } from "../../../Component/Common/Components/DataTable/Filters";
import { ScorecardAdmin, UserAdminRole } from "../../../Component/Common/Constants/UserRoles";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { ScorecardVA } from "../../../Component/Model/Dto/ScorecardLicense";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { defaultObject } from "../../../Component/Model/helpers";
import { userRolesState } from "../../../State/user.Atom";
import { AssignModal } from "../../BEEScorecards/AssignModal";
import { ScorecardLicenseModal } from "../../BEEScorecards/ScorecardLicenses/ScorecardLicenseModal/ScorecardLicenseModal";
import { ScorecardVerificationProcessTracker } from "../../BEEScorecards/ScorecardLicenses/ScorecardVerificationProcessTracker";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { VAClientCertificateModal } from "../Scorecards/CertificatesModal";
import { VALockedScorecardTableMapper } from "./VALockedScorecardsTable";

export const LockedScorecardsPage = () => {
  const userRoles = useRecoilValue(userRolesState);
  const [lockedScorecardsData, setLockedScorecardData] = useState<
    ScorecardVA.ScorecardDataCore[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
  const [userNames, setUserNames] = useState<{ key: string; val: string }[]>(defaultObject);
  const [beeScorecardType, setBeeScorecardType] = useState<{ key: string; val: string }[]>(defaultObject);
  const [yearsRange, setYearsRange] = useState<{ key: string; val: number }[]>(defaultObject);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
  const [showUnlockConfirmationDialog, setShowUnlockConfirmationDialog] = useState<boolean>(false);
  const [selectedScorecardId, setSelectedScorecardId] = useState<string>("");
  const [showViewCertificatesModal, setShowViewCertificatesModal] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [scorecardValue, setScorecardValue] = useState<number[]>([]);
  const [scorecardYear, setScorecardYear] = useState<number[]>([]);
  const [scorecardUser, setScorecardUser] = useState<string[]>([]);
  const [showVAAssignModal, setShowVaAssignModal] = useState<boolean>(false);
  const [showStatusTracker, setShowStatusTracker] = useState<boolean>(false);

  const scorecardSearchRequestCore: ScorecardVA.ScorecardSearchRequestCoreVA = {
    columnSortOptions: columnSortOptions,
    pageNumber: pageNumber,
    pageSize: entriesPerPage.defaultValue!,
    searchValue: searchValue,
    years: scorecardYear,
    scorecardTypeIds: scorecardValue,
    createdByUserNames: scorecardUser,
    onlySearchLockedScorecards: true
  }

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions([sortOption]);
    setPageNumber(1);
  };

  const searchValueChanged = (searchValue: string) => {
    setSearchValue(searchValue);
  };

  const GetUsers = async () => {
    try {
      const users = await UserService.getNonAdminUsers();
      setUserNames(users.data.map((data) => {
        return { key: data.username, val: data.username };
      })
      );
    } catch (error) {
      console.log(error);
    }
  };

  const GetScorecardType = async () => {
    try {
      const scorecardType = await CoreStaticDataService.getBeeScorecardType();
      setBeeScorecardType(scorecardType.data.map((data: any) => {
        return { key: data.name, val: data.id };
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const GetYearsRange = () => {
    const now = new Date();
    const year10YearsAgo = now.getFullYear() - 10;
    const nextYear = now.getFullYear() + 2;
    const rangeOfYears = Array.from(
      { length: nextYear - year10YearsAgo },
      (_, i) => year10YearsAgo + i
    );
    setYearsRange(
      rangeOfYears.map((data) => {
        return { key: data.toString(), val: data };
      })
    );
  };

  const scorecardLicensesFilterData: Array<IFilter> = [
    {
      label: "Scorecard Type:",
      values: beeScorecardType,
      type: FilterType.multiselect,
      propname: "scorecardTypeId",
    },
    {
      label: "Is Consolidated:",
      values: [
        { key: "Yes", val: true },
        { key: "No", val: false },
      ],
      type: FilterType.select,
      propname: "isConsolidated",
    },
    {
      label: "User Name",
      values: userNames,
      type: FilterType.multiselect,
      propname: "createdByUserNames",
    },
    {
      label: "Year:",
      values: yearsRange,
      type: FilterType.multiselect,
      propname: "years",
    },
  ];

  const filtersChanged = async (filterProps: any) => {
    let scorecardTypeId = filterProps["scorecardTypeId"];
    let years = filterProps["years"];
    let createdByUserNames = filterProps["createdByUserNames"];
    let isConsolidated =
      filterProps["isConsolidated"].length == 0
        ? null
        : filterProps["isConsolidated"];
    if (filterProps.isConsolidated !== undefined) {
      scorecardSearchRequestCore.isConsolidated = isConsolidated;
    }
    if (filterProps.scorecardTypeId.length > 0) {
      setScorecardValue(scorecardTypeId);
    }
    if (filterProps.years.length !== 0) {
      setScorecardYear(years);
    }
    if (filterProps.createdByUserNames.length !== 0) {
      setScorecardUser(createdByUserNames);
    }
    searchScorecardData();
  };

  const searchScorecardData = async () => {
    setIsLoading(true);
    try {
      const scorecardSearchResult = await BEEScorecardService.getUserScorecardsCoreVA(scorecardSearchRequestCore);
      setLockedScorecardData(scorecardSearchResult.data.results);
      setFilteredItemsCount(scorecardSearchResult.data.filteredItemsCount);
      setTotalItemsCount(scorecardSearchResult.data.totalItemsCount);
    } catch (error) {
      setLockedScorecardData(defaultObject);
      setFilteredItemsCount(0);
      setTotalItemsCount(0);
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignUser = (id: string) => {
    setSelectedScorecardId(id);
    setShowVaAssignModal(true);
  }

  const closeAssignUserModal = () => {
    setSelectedScorecardId("");
    setShowVaAssignModal(false);
  }

  const handleDelete = async () => {
    try {
      await BEEScorecardService.deleteCoreScorecard(selectedScorecardId);
      toast.success("Successfully deleted scorecard");
      searchScorecardData();
    } catch (error) {
      console.log(error);
    } finally {
      handleCloseConfirmation();
    }
  };

  const handleViewCertificates = (id: string) => {
    setShowViewCertificatesModal(true);
    setSelectedScorecardId(id);
  }

  const handleCloseViewCertificatesModal = () => {
    setShowViewCertificatesModal(false);
  }


  const handleCloseConfirmation = () => {
    setShowDeleteConfirmationDialog(false);
    setSelectedScorecardId("");
  };

  const showUnlockConfirmation = (id: string) => {
    setSelectedScorecardId(id);
    setShowUnlockConfirmationDialog(true);
  };

  const handleUnlockScorecard = async () => {
    try {
      await BEEScorecardService.unlockScorecard(selectedScorecardId);
      toast.success("Successfully unlocked scorecard");
      searchScorecardData();
    } catch (error) {
      console.log(error);
    } finally {
      handleCloseUnlockModal();
    }
  }

  const handleCloseUnlockModal = () => {
    setShowUnlockConfirmationDialog(false);
  }

  const showDeleteConfirmation = (id: string) => {
    setSelectedScorecardId(id);
    setShowDeleteConfirmationDialog(true);
  };

  const handleStatusTracker = (id: string) => {
    setShowStatusTracker(true);
    setSelectedScorecardId(id);
  }

  const handleCloseStatusTracker = () => {
    setShowStatusTracker(false);
  }

  const confirmDeleteMessage = "Are you sure you want to delete this scorecard?";
  const confirmUnlockMessage = "Are you sure you want to unlock this scorecard?";

  useEffect(() => {
    GetUsers();
    GetScorecardType();
    GetYearsRange();
  }, []);

  useEffect(() => {
    searchScorecardData();
  }, [entriesPerPage, pageNumber, columnSortOptions, searchValue, scorecardYear, scorecardUser, scorecardValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const lockedScorecardTableData = VALockedScorecardTableMapper(
    lockedScorecardsData,
    handleAssignUser,
    showUnlockConfirmation,
    showDeleteConfirmation,
    handleViewCertificates,
    handleStatusTracker,
    userRoles
  );

  const handleAddScoredcard = () => {
    setShowModal(true);
  };

  const bulkActions = [
    {
      label: "Add Scorecard",
      action: handleAddScoredcard,
    },
  ];

  return (
    <>
      <DataTable
        table={lockedScorecardTableData}
        canSearch
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemsCount}
        totalItemsCount={totalItemsCount}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        searchValueChanged={searchValueChanged}
        isLoading={isLoading}
        manualSorting={true}
        columnSortingChanged={columnSortingChanged}
        filters={scorecardLicensesFilterData}
        filtersChanged={filtersChanged}
        {...((userRoles.includes(ScorecardAdmin) || userRoles.includes(UserAdminRole)) ? { bulkActions } : {})}
      ></DataTable>
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="xl"
      >
        <CustomDialogTitle
          id="customized-dialog-title"
          onClose={() => setShowModal(false)}
        >
          Add Scorecard
        </CustomDialogTitle>
        <DialogContent>
          <ScorecardLicenseModal modalDescription={"Summary of Scorecard Licenses"} />
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        showDialog={showDeleteConfirmationDialog}
        handleAccept={handleDelete}
        handleCancel={handleCloseConfirmation}
        heading={"Confirm Delete"}
        message={confirmDeleteMessage}
      />
      <ConfirmationDialog
        showDialog={showUnlockConfirmationDialog}
        handleAccept={handleUnlockScorecard}
        handleCancel={handleCloseUnlockModal}
        heading={"Confirm Unlock"}
        message={confirmUnlockMessage}
      />
      {showViewCertificatesModal && <VAClientCertificateModal showModal={showViewCertificatesModal} handleCancel={handleCloseViewCertificatesModal} scorecardId={selectedScorecardId} />}
      {showVAAssignModal && <AssignModal isOrganisationUserAccess={false} id={selectedScorecardId} showAssignModal={showVAAssignModal} closeAssignModal={closeAssignUserModal} />}
      {showStatusTracker && <ScorecardVerificationProcessTracker scorecardId={selectedScorecardId} showScorecardVerificationProcessTrackerModal={showStatusTracker} closeScorecardVerificationProcessTrackerModal={handleCloseStatusTracker} />}
    </>
  );
};
